import { Link } from "react-router-dom";
import { LeafTile } from "./Logo";
import { Instagram, Linkedin, Youtube, Mail, Phone, MapPin, MessageCircle } from "lucide-react";
import { useApp } from "../context/AppContext";
import { waLink } from "../lib/api";

export default function Footer() {
  const { config } = useApp();
  const { brand } = config;

  const companyLinks = [
    ["How It Works", "how"],
    ["Services", "services"],
    ["CARZA Nearby", "/aggregator", true],
    ["For Business", "/for-business", true],
    ["Partners", "/partners", true],
    ["Investors", "/investors", true],
  ];

  return (
    <footer id="contact" className="relative border-t border-white/5 bg-coal" data-testid="site-footer">
      <div className="container-x grid grid-cols-1 gap-12 py-16 sm:grid-cols-2 lg:grid-cols-12 lg:py-20">
        <div className="lg:col-span-4">
          <Link to="/" aria-label="CARZA home">
            <LeafTile size={40} />
          </Link>
          <p className="mt-5 font-display text-xl font-extrabold lowercase tracking-tight text-paper">carza</p>
          <p className="mt-3 max-w-xs text-sm leading-relaxed text-mist">{brand.tagline}</p>
          <div className="mt-7 flex items-center gap-3">
            {[
              { icon: Instagram, label: "Instagram" },
              { icon: Linkedin, label: "LinkedIn" },
              { icon: Youtube, label: "YouTube" },
            ].map(({ icon: Icon, label }) => (
              <a
                key={label}
                href="#"
                onClick={(e) => e.preventDefault()}
                aria-label={`${label} (coming soon)`}
                title={`${label} — coming soon`}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-mist transition-all hover:border-carza/50 hover:text-carza-lite"
                data-testid={`footer-social-${label.toLowerCase()}`}
              >
                <Icon size={16} />
              </a>
            ))}
          </div>
        </div>

        <div className="lg:col-span-3">
          <h4 className="font-mono text-[10px] uppercase tracking-[0.28em] text-mist-2">Company</h4>
          <ul className="mt-5 flex flex-col gap-3 text-sm">
            {companyLinks.map(([label, target, isRoute]) => (
              <li key={label}>
                {isRoute ? (
                  <Link to={target} className="text-mist transition-colors hover:text-carza-lite" data-testid={`footer-link-${target.replace("/", "") || target}`}>
                    {label}
                  </Link>
                ) : (
                  <a href={`#${target}`} className="text-mist transition-colors hover:text-carza-lite" data-testid={`footer-link-${target}`}>
                    {label}
                  </a>
                )}
              </li>
            ))}
            <li>
              <Link to="/login" className="text-mist transition-colors hover:text-carza-lite" data-testid="footer-link-login">Customer Login</Link>
            </li>
          </ul>
        </div>

        <div className="lg:col-span-2">
          <h4 className="font-mono text-[10px] uppercase tracking-[0.28em] text-mist-2">Legal</h4>
          <ul className="mt-5 flex flex-col gap-3 text-sm">
            <li><Link to="/privacy" className="text-mist transition-colors hover:text-carza-lite" data-testid="footer-link-privacy">Privacy Policy</Link></li>
            <li><Link to="/terms" className="text-mist transition-colors hover:text-carza-lite" data-testid="footer-link-terms">Terms &amp; Conditions</Link></li>
            <li><Link to="/admin" className="text-mist-2/60 transition-colors hover:text-carza-lite" data-testid="footer-link-admin">Admin</Link></li>
          </ul>
        </div>

        <div className="lg:col-span-3">
          <h4 className="font-mono text-[10px] uppercase tracking-[0.28em] text-mist-2">Contact</h4>
          <ul className="mt-5 flex flex-col gap-3.5 text-sm text-mist">
            <li className="flex items-center gap-3">
              <Mail size={15} className="text-carza" />
              <a href={`mailto:${brand.email}`} className="transition-colors hover:text-paper" data-testid="footer-email">{brand.email}</a>
            </li>
            <li className="flex items-center gap-3">
              <Phone size={15} className="text-carza" />
              <a href={`tel:${brand.phone.replace(/\s/g, "")}`} className="transition-colors hover:text-paper" data-testid="footer-phone">{brand.phone}</a>
            </li>
            <li className="flex items-center gap-3">
              <MapPin size={15} className="text-carza" />
              <span>{brand.location}</span>
            </li>
          </ul>
          <a
            href={waLink(brand.whatsapp)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-outline mt-6 w-full py-3 text-[13px]"
            data-testid="footer-whatsapp-btn"
          >
            <MessageCircle size={16} className="text-carza" /> WhatsApp Us
          </a>
        </div>
      </div>

      <div className="border-t border-white/5">
        <div className="container-x flex flex-col items-center justify-between gap-3 py-6 text-center sm:flex-row sm:text-left">
          <p className="font-mono text-[11px] tracking-wide text-mist-2">© 2026 CARZA. All rights reserved.</p>
          <p className="font-mono text-[11px] tracking-wide text-mist-2">{brand.brand_tagline}</p>
        </div>
      </div>
    </footer>
  );
}
